import {
  Box,
  HStack,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { CherryPayApi } from "../../api/models";
import { InfoPanel } from "../Utilities/InfoPanel";
import {
  ImagePreview,
  NotificationPreviewType,
} from "../ImagePreview/ImagePreview";
import { useBusinessContext } from "../../context/ModelContext";
import { NotificationScheduleDetails } from "../NotificationScheduleDetails/NotificationScheduleDetails";
import { NotificationFormValues } from "../../forms/Notifications/types";
import { isEmptyStr } from "../../util/StringUtil";

interface NotificationPreviewProps {
  notification: CherryPayApi.CherryPassNotificationScheduleDetail;
  memberCount?: number | null;
  geoLocations: CherryPayApi.GeoLocation[];
  selectedGeoLocationId: string | null;
  isGeoSectionOpen: boolean;
  notificationType: NotificationPreviewType;
  onNotificationTypeChange: (value: NotificationPreviewType) => void;
}

const LOCATION_PREVIEW_PLACEHOLDER =
  "Target specific venue locations. Configure this in the Geo-Locations section.";

export const NotificationPreview: React.FC<NotificationPreviewProps> = ({
  notification,
  memberCount,
  geoLocations,
  selectedGeoLocationId,
  isGeoSectionOpen,
  notificationType,
  onNotificationTypeChange,
}) => {
  const businessSummary = useBusinessContext();
  const { values } = useFormikContext<NotificationFormValues>();

  const [tabIndex, setTabIndex] = useState(isGeoSectionOpen ? 0 : 1);

  useEffect(() => {
    setTabIndex(isGeoSectionOpen ? 0 : 1);
  }, [isGeoSectionOpen]);

  const hasGeoLocations = geoLocations.length > 0;
  const selectedLocation =
    geoLocations.find((l) => l.GeoLocationId === selectedGeoLocationId) ?? null;
  const selectedOverride = selectedLocation?.Meta?.Id
    ? values.locationMessageOverrides?.[selectedLocation.Meta.Id]
    : undefined;
  const isOverridden = !isEmptyStr(selectedOverride);
  const selectedLocationName =
    selectedLocation?.DisplayName || "Unnamed Location";

  const rawNotificationMessage = notification.PassUpdateMessage ?? "";
  const locationMessage = selectedLocation
    ? isOverridden
      ? selectedOverride!
      : selectedLocation.RelevantText ?? ""
    : "";

  const isGeoMode = hasGeoLocations && notificationType === "geo";
  const isGoogleTabDisabled = isGeoMode;

  let lockScreenMessage: string;
  let backOfPassMessage: string | null;

  if (isGeoMode) {
    lockScreenMessage = locationMessage;
    backOfPassMessage = null;
  } else {
    lockScreenMessage = rawNotificationMessage;
    backOfPassMessage = rawNotificationMessage;
  }

  const scheduleDetails = (
    <NotificationScheduleDetails
      notification={notification}
      isEditing={true}
      memberCount={memberCount}
      businessId={businessSummary.BusinessId}
    />
  );

  const selectedTabStyles = {
    color: "cherryButton.800",
    borderColor: "cherryButton.800",
    fontWeight: "bold",
  };

  const locationPreviewContent = selectedLocation ? (
    <VStack align="start" spacing={4} px={8} pb={8} pt={6}>
      <HStack alignItems="flex-start">
        <Text fontWeight="medium" minWidth="160px">
          Previewing:
        </Text>
        <Text>{selectedLocationName}</Text>
      </HStack>
      <HStack alignItems="center">
        <Text fontWeight="medium" minWidth="160px">
          Message Type:
        </Text>
        {isOverridden ? (
          <Box
            bg="campaignMessage.bg"
            color="campaignMessage.label"
            borderRadius="full"
            borderColor="gray.200"
            border={"1px solid"}
            px={3}
            py={1}
            fontSize="sm"
            fontWeight="600"
          >
            CAMPAIGN MESSAGE
          </Box>
        ) : (
          <Box
            bg="gray.100"
            color="gray.700"
            borderRadius="full"
            borderColor="gray.200"
            border={"1px solid"}
            px={3}
            py={1}
            fontSize="sm"
          >
            DEFAULT LOCATION MESSAGE
          </Box>
        )}
      </HStack>
      {isOverridden && (
        <Text color="gray.600">
          Members entering {selectedLocationName} during the campaign will see
          this message instead of the default venue message.
        </Text>
      )}
    </VStack>
  ) : (
    <Box px={8} pb={8} pt={6}>
      <Text color="gray.400">{LOCATION_PREVIEW_PLACEHOLDER}</Text>
    </Box>
  );

  return (
    <Box w="100%" p={4}>
      <Stack spacing={6} w="100%">
        <InfoPanel>
          The business logo shown on the push notification and the pass design
          on the back of the pass are for <b>example purposes only</b>. Your
          actual business logo and pass design will be applied when published.{" "}
        </InfoPanel>

        <VStack align="start" w="100%">
          <Box p={4} borderRadius="md" w="100%">
            <ImagePreview
              businessDisplayName={businessSummary.DisplayName}
              lockScreenMessage={lockScreenMessage}
              backOfPassMessage={backOfPassMessage}
              isGoogleTabDisabled={isGoogleTabDisabled}
              notificationTypeToggle={
                hasGeoLocations
                  ? {
                      value: notificationType,
                      onChange: onNotificationTypeChange,
                    }
                  : undefined
              }
            />
            {hasGeoLocations ? (
              <Box
                background="white"
                borderRadius="lg"
                boxShadow="md"
                mt={4}
                overflow="hidden"
              >
                <Tabs
                  index={tabIndex}
                  onChange={setTabIndex}
                  isFitted
                  variant="line"
                >
                  <TabList>
                    <Tab
                      color="cherryGrey.1100"
                      fontWeight="semibold"
                      py={5}
                      _selected={selectedTabStyles}
                    >
                      Location Preview
                    </Tab>
                    <Tab
                      color="cherryGrey.1100"
                      fontWeight="semibold"
                      py={5}
                      _selected={selectedTabStyles}
                    >
                      Campaign Summary
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel p={0}>{locationPreviewContent}</TabPanel>
                    <TabPanel px={8} pb={8} pt={0}>
                      {scheduleDetails}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
            ) : (
              <Box
                background="white"
                borderRadius={2}
                px={8}
                pt={0}
                pb={8}
                boxShadow="md"
              >
                {scheduleDetails}
              </Box>
            )}
          </Box>
        </VStack>
      </Stack>
    </Box>
  );
};
